import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  TextField, 
  IconButton, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Chip,
  Tooltip,
  LinearProgress,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Divider,
  Breadcrumbs,
  Link as MuiLink
} from '@mui/material';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  Archive, 
  ChevronRight,
  ExternalLink,
  Layers,
  Calendar,
  Users as UsersIcon,
  Settings,
  Eye,
  ArrowLeft,
  Target,
  Trophy,
  Rocket,
  Code,
  Database,
  Layout,
  Clock,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
  Hash,
  Activity,
  User,
  GitBranch,
  BarChart3,
  CalendarDays
} from 'lucide-react';
import axios from 'axios';
import { useParams, useNavigate, Link } from 'react-router-dom';
import API_BASE_URL from '../../Config/config';
import ProjectWizard from './projectWizard/ProjectWizard';
// Single source of truth for reading a project row: the same normalisers the
// wizard uses. mergeProjectDetails folds the details JSONB overflow into the
// flat object (defensively — the API already flattens, this survives an older
// deployment), and toList makes array-ish columns safe to .map().
import { mergeProjectDetails, toList } from './projectWizard/projectFormModel';
// Imported explicitly rather than added to the large MUI destructure above,
// so this addition is obvious in review and cannot disturb that list.
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Edit was previously unreachable: the button below had no onClick and no
  // form existed behind it. These two pieces of state are the whole entry
  // point — the wizard owns fetching and populating the project itself.
  const [editOpen, setEditOpen] = useState(false);
  const [techStacks, setTechStacks] = useState([]);
  const [editToast, setEditToast] = useState('');
  const [openAddVersion, setOpenAddVersion] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' });
  // null → the dialog creates a new version; an id → it edits that version's
  // details + schedule via PUT. One dialog serves both paths so the schedule
  // fields can never drift between create and edit.
  const [editVersionId, setEditVersionId] = useState(null);
  const [versionError, setVersionError] = useState('');
  const EMPTY_VERSION_FORM = {
    name: '',
    description: '',
    sprint_duration: '2 weeks',
    release_target: '',
    status: 'planning',
    team_size: '',
    // Schedule fields — the backend (validateSchedule + date-driven
    // enrollment states) already supports all of these; the admin UI just
    // never exposed them, which is why every student card showed TBD.
    start_date: '',
    end_date: '',
    enrollment_opens: '',
    enrollment_closes: '',
    sprint_start_date: '',
    sprint_end_date: '',
    max_members: ''
  };
  const [versionForm, setVersionForm] = useState(EMPTY_VERSION_FORM);

  // yyyy-mm-dd for <input type="date">; tolerant of nulls and timestamps.
  const toDateInput = (d) => {
    if (!d) return '';
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? '' : t.toISOString().slice(0, 10);
  };
  const fmtDate = (d) => {
    if (!d) return null;
    const t = new Date(d);
    return Number.isNaN(t.getTime()) ? null : t.toLocaleDateString();
  };

  const openCreateVersion = () => {
    setEditVersionId(null);
    setVersionError('');
    setVersionForm(EMPTY_VERSION_FORM);
    setOpenAddVersion(true);
  };

  const openEditVersion = (v) => {
    setEditVersionId(v.id);
    setVersionError('');
    setVersionForm({
      name: v.name || '',
      description: v.description || '',
      sprint_duration: v.sprint_duration || '',
      release_target: toDateInput(v.release_target),
      status: v.status || 'planning',
      team_size: v.team_size ?? '',
      start_date: toDateInput(v.start_date),
      end_date: toDateInput(v.end_date),
      enrollment_opens: toDateInput(v.enrollment_opens),
      enrollment_closes: toDateInput(v.enrollment_closes),
      sprint_start_date: toDateInput(v.sprint_start_date),
      sprint_end_date: toDateInput(v.sprint_end_date),
      max_members: v.max_members ?? ''
    });
    setOpenAddVersion(true);
  };

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/tech-stacks`);
        if (cancelled) return;
        const list = res.data && res.data.data;
        setTechStacks(Array.isArray(list) ? list : []);
      } catch (err) {
        // Non-fatal: without this list the wizard simply falls back to the
        // tech_stack_id already stored on the project.
        if (!cancelled) setTechStacks([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Called by the wizard after a successful PUT. The updated record comes
  // back in the response, so the page updates in place — no reload, and no
  // second round-trip unless the server returned nothing usable.
  const handleProjectSaved = (updated) => {
    setEditOpen(false);
    if (updated && typeof updated === 'object') {
      // `updated` is the FLAT project object from the PUT response, but this
      // page's state is the { project, versions } envelope. The old spread
      // merged the flat fields NEXT TO `project` instead of INTO it, so the
      // page kept rendering the pre-edit values until a full reload — the
      // "saved but details doesn't update" symptom. Merge into the envelope's
      // project (keeping joined fields like tech_stack_name the PUT response
      // doesn't carry), and keep versions as they were.
      setData((prev) => ({
        ...(prev || {}),
        project: { ...((prev && prev.project) || {}), ...updated },
        versions: (prev && prev.versions) || []
      }));
    } else {
      fetchProjectDetails();
    }
    setEditToast('Project updated successfully');
  };

  const fetchProjectDetails = async () => {
    try {
      setLoading(true);
      console.log(`[ProjectDetails] Fetching details for ID: ${id}`);
      const res = await axios.get(`${API_BASE_URL}/api/nextgen-builder/projects/${id}`);
      console.log('[ProjectDetails] API Response:', res.data);
      if (res.data.success) {
        setData(res.data.data);
      } else {
        console.error('[ProjectDetails] API Error:', res.data.message);
      }
    } catch (err) {
      console.error('Failed to fetch project details', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVersion = async () => {
    try {
      setVersionError('');
      const orNull = (v) => (v === '' || v == null ? null : v);
      // team_size is an INTEGER column — never let '' through. If neither the
      // form nor the project default parses as a number, fall back to 25
      // (the platform's historical capacity cap).
      const formTeam = parseInt(versionForm.team_size, 10);
      const defaultTeam = parseInt(data.project.default_team_size, 10);
      const payload = {
        name: versionForm.name,
        description: versionForm.description,
        sprint_duration: versionForm.sprint_duration,
        release_target: orNull(versionForm.release_target),
        status: versionForm.status,
        team_size: Number.isFinite(formTeam) ? formTeam : (Number.isFinite(defaultTeam) ? defaultTeam : 25),
        start_date: orNull(versionForm.start_date),
        end_date: orNull(versionForm.end_date),
        enrollment_opens: orNull(versionForm.enrollment_opens),
        enrollment_closes: orNull(versionForm.enrollment_closes),
        sprint_start_date: orNull(versionForm.sprint_start_date),
        sprint_end_date: orNull(versionForm.sprint_end_date),
        max_members: orNull(versionForm.max_members)
      };
      const res = editVersionId
        ? await axios.put(`${API_BASE_URL}/api/nextgen-builder/versions/${editVersionId}`, payload)
        : await axios.post(`${API_BASE_URL}/api/nextgen-builder/versions`, { ...payload, project_id: id });
      if (res.data.success) {
        setOpenAddVersion(false);
        setEditVersionId(null);
        fetchProjectDetails();
        setEditToast(editVersionId ? 'Version schedule updated' : 'Version created');
      } else {
        setVersionError(res.data.message || 'Failed to save version');
      }
    } catch (err) {
      // The backend's schedule validator returns [{ field, message }] —
      // surface the exact rule that failed instead of a generic error.
      const apiErrors = err?.response?.data?.errors;
      const msg = Array.isArray(apiErrors) && apiErrors.length
        ? apiErrors.map((e) => e.message || e).join(' · ')
        : (err?.response?.data?.message || 'Failed to save version');
      setVersionError(msg);
      console.error('Failed to save version', err);
    }
  };

  const handleDeleteVersion = async () => {
    try {
      const res = await axios.delete(`${API_BASE_URL}/api/nextgen-builder/versions/${deleteDialog.id}`);
      if (res.data.success) {
        setDeleteDialog({ open: false, id: null, name: '' });
        fetchProjectDetails();
        setEditToast('Version deleted successfully');
      }
    } catch (err) {
      console.error('Failed to delete version', err);
    }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={10} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}><CircularProgress /></Box>;
  if (!data || !data.project) {
    return (
      <Box p={4} sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh' }}>
        <Typography variant="h6" color="error" gutterBottom>Project Not Found</Typography>
        <Typography variant="body2" mb={3}>The project you are looking for does not exist or has been deleted.</Typography>
        <Button variant="contained" onClick={() => navigate('/nextgen/projects')}>
          Back to Projects
        </Button>
      </Box>
    );
  }

  const { project: rawProject, versions = [] } = data;
  const project = mergeProjectDetails(rawProject);

  const calculateTimelineStatus = (startDate, endDate) => {
    if (!startDate || !endDate) return { label: 'Not Set', color: '#64748b', bgcolor: '#f1f5f9' };
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (today < start) {
      return { label: 'Upcoming', color: '#0369a1', bgcolor: '#e0f2fe' };
    } else if (today >= start && today <= end) {
      return { label: 'Active', color: '#15803d', bgcolor: '#dcfce7' };
    } else {
      return { label: 'Completed', color: '#475569', bgcolor: '#f1f5f9' };
    }
  };

  const timelineStatus = calculateTimelineStatus(project.start_date, project.end_date);

  const getDurationDays = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const today = new Date();
    const end = new Date(endDate);
    const diffTime = end - today;
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  const getTimelineProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    if (today < start) return 0;
    if (today > end) return 100;

    const total = end - start;
    const elapsed = today - start;
    return Math.round((elapsed / total) * 100);
  };

  const InfoItem = ({ icon: Icon, label, value, color = '#64748b' }) => (
    <Box display="flex" alignItems="center" gap={1.5} sx={{ minWidth: '120px' }}>
      <Box sx={{ color, bgcolor: `${color}10`, p: 0.75, borderRadius: '8px', display: 'flex' }}>
        <Icon size={16} />
      </Box>
      <Box>
        <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1, mb: 0.5, fontWeight: 500 }}>
          {label}
        </Typography>
        <Typography variant="body2" sx={{ color: '#1e293b', fontWeight: '700', fontSize: '0.875rem' }}>
          {value || 'Not provided'}
        </Typography>
      </Box>
    </Box>
  );

  const SectionCard = ({ title, icon: Icon, children, action }) => (
    <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', height: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box sx={{ color: '#4f46e5', bgcolor: '#eef2ff', p: 1, borderRadius: '10px', display: 'flex' }}>
              <Icon size={20} />
            </Box>
            <Typography variant="h6" fontWeight="700" color="#1e293b">{title}</Typography>
          </Box>
          {action}
        </Box>
        {children}
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', width: '100%', pb: 8 }}>
      <Box sx={{ px: 4, pt: 2, pb: 4, borderBottom: '1px solid #e2e8f0', bgcolor: 'white' }}>
        <Breadcrumbs separator={<ChevronRight size={14} />} sx={{ mb: 2.5 }}>
          <MuiLink component={Link} to="/nextgen/projects" underline="hover" color="#94a3b8" sx={{ display: 'flex', alignItems: 'center', fontSize: '0.875rem', fontWeight: 500 }}>
            Projects
          </MuiLink>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>{project.name}</Typography>
        </Breadcrumbs>

        <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={3}>
          <Box display="flex" alignItems="flex-start" gap={2.5}>
            <IconButton 
              onClick={() => navigate('/nextgen/projects')} 
              sx={{ 
                color: '#64748b', 
                bgcolor: '#f8fafc',
                border: '1px solid #e2e8f0',
                '&:hover': { bgcolor: '#f1f5f9' },
                borderRadius: '12px',
                p: 1.25,
                mt: 0.5
              }}
            >
              <ArrowLeft size={22} />
            </IconButton>
            <Box>
              <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                <Typography variant="h3" fontWeight="800" sx={{ color: '#1e293b', letterSpacing: '-0.02em' }}>
                  {project.name}
                </Typography>
                <Chip 
                  label={timelineStatus.label}
                  size="small" 
                  sx={{ 
                    bgcolor: timelineStatus.bgcolor,
                    color: timelineStatus.color,
                    fontWeight: '800',
                    borderRadius: '8px',
                    textTransform: 'uppercase',
                    fontSize: '0.7rem',
                    px: 0.5,
                    height: '24px'
                  }} 
                />
              </Box>

              <Box display="flex" flexWrap="wrap" gap={3} mt={2}>
                <InfoItem icon={Layers} label="Stack" value={project.tech_stack_name} color="#4f46e5" />
                <InfoItem icon={Activity} label="Difficulty" value={project.difficulty_level} color="#0891b2" />
                <InfoItem icon={UsersIcon} label="Team Size" value={`${project.default_team_size || 0} Members`} color="#059669" />
                <InfoItem icon={Hash} label="Versions" value={`${project.total_versions || 0} Total`} color="#7c3aed" />
                <InfoItem icon={CalendarDays} label="Created" value={project.created_at ? new Date(project.created_at).toLocaleDateString() : 'N/A'} color="#ea580c" />
              </Box>
            </Box>
          </Box>

          <Box display="flex" gap={1.5}>
            <Button 
              variant="outlined" 
              startIcon={<Edit size={18} />} 
              onClick={() => setEditOpen(true)}
              disabled={loading || !data}
              sx={{ 
                borderRadius: '12px',
                textTransform: 'none', 
                fontWeight: '700',
                borderColor: '#e2e8f0',
                color: '#475569',
                px: 2.5,
                '&:hover': { borderColor: '#cbd5e1', bgcolor: '#f8fafc' }
              }}
            >
              Edit Project
            </Button>
            <Button 
              variant="contained" 
              startIcon={<Plus size={18} />}
              onClick={openCreateVersion}
              sx={{ 
                bgcolor: '#4f46e5', 
                '&:hover': { bgcolor: '#4338ca' }, 
                borderRadius: '12px',
                textTransform: 'none', 
                fontWeight: '700',
                px: 2.5,
                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.25)'
              }}
            >
              New Version
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ px: 4, mt: 4 }}>
        <Grid container spacing={3}>
          {/* Main Content Column */}
          <Grid item xs={12} md={8}>
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Project Summary */}
              <Card elevation={0} sx={{ borderRadius: '16px', border: '1px solid #eef2ff', bgcolor: '#f5f3ff' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" gap={2}>
                    <Box sx={{ color: '#7c3aed', mt: 0.5 }}>
                      <Rocket size={24} />
                    </Box>
                    <Box>
                      <Typography variant="h6" fontWeight="800" color="#4c1d95" mb={1}>Project Summary</Typography>
                      <Typography variant="body1" sx={{ color: '#5b21b6', lineHeight: 1.6, fontWeight: 500, fontSize: '1.05rem' }}>
                        {project.project_summary || 'A professional product specification for students to learn and build.'}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              {/* Description */}
              <SectionCard title="Detailed Description" icon={Layout}>
                <Typography variant="body1" color="#475569" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {project.description || 'No detailed description provided.'}
                </Typography>
              </SectionCard>

              {/* Vision */}
              <SectionCard title="Vision" icon={Target}>
                <Typography variant="body1" color="#475569" sx={{ lineHeight: 1.7, fontStyle: project.vision ? 'normal' : 'italic' }}>
                  {project.vision || 'The vision for this project has not been defined yet.'}
                </Typography>
              </SectionCard>

              {/* Learning Objectives & Outcomes */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <SectionCard title="Learning Objectives" icon={Trophy}>
                    {project.learning_objectives ? (
                      <Box component="ul" sx={{ m: 0, pl: 2, color: '#475569' }}>
                        {project.learning_objectives.split('\n').filter(line => line.trim()).map((obj, i) => (
                          <Box component="li" key={i} sx={{ mb: 1, '&::marker': { color: '#4f46e5' } }}>
                            <Typography variant="body2">{obj}</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                    )}
                  </SectionCard>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SectionCard title="Expected Outcomes" icon={CheckCircle2}>
                    {project.expected_outcomes ? (
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        {project.expected_outcomes.split('\n').filter(line => line.trim()).map((outcome, i) => (
                          <Box key={i} display="flex" gap={1.5}>
                            <Box sx={{ color: '#059669', mt: 0.25 }}>
                              <CheckCircle2 size={16} />
                            </Box>
                            <Typography variant="body2" color="#475569">{outcome}</Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>

              {/* Project Purpose — hidden entirely when nothing is filled in */}
              {(project.problem_statement || project.business_value || project.student_benefits || project.career_benefits) && (
                <SectionCard title="Project Purpose" icon={HelpCircle}>
                  <Grid container spacing={3}>
                    {[
                      { label: 'Problem Statement', value: project.problem_statement },
                      { label: 'Business Value', value: project.business_value },
                      { label: 'Student Benefits', value: project.student_benefits },
                      { label: 'Career Benefits', value: project.career_benefits },
                    ].filter(item => item.value).map((item) => (
                      <Grid item xs={12} sm={6} key={item.label}>
                        <Typography variant="caption" color="#94a3b8" fontWeight="700" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>{item.label}</Typography>
                        <Typography variant="body2" color="#475569" sx={{ mt: 0.5, lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{item.value}</Typography>
                      </Grid>
                    ))}
                  </Grid>
                </SectionCard>
              )}

              {/* Prerequisites & Skills */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <SectionCard title="Prerequisites" icon={AlertCircle}>
                    {toList(project.prerequisites).length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {toList(project.prerequisites).map((p, i) => (
                          <Chip key={i} label={p} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                    )}
                  </SectionCard>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SectionCard title="Skills Students Will Learn" icon={Trophy}>
                    {toList(project.recommended_skills).length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {toList(project.recommended_skills).map((s, i) => (
                          <Chip key={i} label={s} size="small" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                    )}
                  </SectionCard>
                </Grid>
              </Grid>

              {/* Deliverables & Certificates */}
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <SectionCard title="Deliverables" icon={CheckCircle2}>
                    {toList(project.deliverables).length > 0 ? (
                      <Box display="flex" flexDirection="column" gap={1}>
                        {toList(project.deliverables).map((d, i) => (
                          <Box key={i} display="flex" gap={1.5} alignItems="center">
                            <CheckCircle2 size={15} color="#4f46e5" />
                            <Typography variant="body2" color="#475569" sx={{ textTransform: 'capitalize' }}>
                              {String(d).replace(/_/g, ' ')}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                    )}
                  </SectionCard>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SectionCard title="Certificates" icon={Trophy}>
                    {(() => {
                      const certs = [
                        { on: project.completion_certificate, label: 'Completion Certificate' },
                        { on: project.excellence_badge, label: 'Excellence Badge' },
                        { on: project.industry_recommendation, label: 'Industry Recommendation' },
                        { on: project.linkedin_badge, label: 'LinkedIn Badge' },
                      ].filter(c => c.on === true || c.on === 'true');
                      return certs.length > 0 ? (
                        <Box display="flex" flexWrap="wrap" gap={1}>
                          {certs.map((c) => (
                            <Chip key={c.label} label={c.label} size="small" sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 700 }} />
                          ))}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                      );
                    })()}
                  </SectionCard>
                </Grid>
              </Grid>
            </Box>
          </Grid>

          {/* Sidebar Column */}
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" gap={3}>
              {/* Timeline Card */}
              <SectionCard title="Project Timeline" icon={Clock}>
                <Box mb={3}>
                  <Box display="flex" justifyContent="space-between" mb={1}>
                    <Typography variant="caption" fontWeight="700" color="#64748b">Progress</Typography>
                    <Typography variant="caption" fontWeight="800" color="#1e293b">{getTimelineProgress(project.start_date, project.end_date)}%</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={getTimelineProgress(project.start_date, project.end_date)}
                    sx={{
                      height: 10,
                      borderRadius: 5,
                      bgcolor: '#f1f5f9',
                      '& .MuiLinearProgress-bar': {
                        bgcolor: timelineStatus.color,
                        borderRadius: 5
                      }
                    }}
                  />
                </Box>

                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">Start Date</Typography>
                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                      {project.start_date ? new Date(project.start_date).toLocaleDateString() : 'TBD'}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">End Date</Typography>
                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                      {project.end_date ? new Date(project.end_date).toLocaleDateString() : 'TBD'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">Enrollment Opens</Typography>
                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                      {project.enrollment_opens ? new Date(project.enrollment_opens).toLocaleDateString() : 'TBD'}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">Enrollment Closes</Typography>
                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                      {project.enrollment_closes ? new Date(project.enrollment_closes).toLocaleDateString() : 'TBD'}
                    </Typography>
                  </Box>
                  <Divider />
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">Total Duration</Typography>
                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                      {getDurationDays(project.start_date, project.end_date)} Days
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">Remaining</Typography>
                    <Typography variant="body2" fontWeight="700" color="#0369a1">
                      {getDaysRemaining(project.end_date)} Days
                    </Typography>
                  </Box>
                </Box>
              </SectionCard>

              {/* Technical Specifications */}
              <SectionCard title="Tech Specifications" icon={Settings}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" gap={2}>
                    <Box sx={{ color: '#4f46e5', mt: 0.5 }}><Code size={18} /></Box>
                    <Box>
                      <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>Language & Framework</Typography>
                      <Typography variant="body2" fontWeight="700" color="#1e293b">{[project.primary_language, project.framework].filter(Boolean).join(' / ') || 'Not provided'}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Box sx={{ color: '#0891b2', mt: 0.5 }}><Database size={18} /></Box>
                    <Box>
                      <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>Database</Typography>
                      <Typography variant="body2" fontWeight="700" color="#1e293b">{project.database_type || 'Not provided'}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Box sx={{ color: '#7c3aed', mt: 0.5 }}><BarChart3 size={18} /></Box>
                    <Box>
                      <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>Architecture</Typography>
                      <Typography variant="body2" fontWeight="700" color="#1e293b">{project.architecture || 'Not provided'}</Typography>
                    </Box>
                  </Box>
                  <Box display="flex" gap={2}>
                    <Box sx={{ color: '#ea580c', mt: 0.5 }}><GitBranch size={18} /></Box>
                    <Box>
                      <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase', fontSize: '0.7rem' }}>Repository</Typography>
                      {project.repository_url ? (
                        <MuiLink href={project.repository_url} target="_blank" sx={{ color: '#4f46e5', fontWeight: '700', textDecoration: 'none', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          View Repo <ExternalLink size={14} />
                        </MuiLink>
                      ) : (
                        <Typography variant="body2" fontWeight="700" color="#1e293b">Not linked</Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Full stack chips — every technology array the wizard collects */}
                  {(() => {
                    const chips = [
                      ...toList(project.tech_stacks), ...toList(project.languages),
                      ...toList(project.cloud_platforms), ...toList(project.cicd_tools),
                      ...toList(project.ai_tools), ...toList(project.third_party_integrations),
                    ];
                    const unique = Array.from(new Set(chips.map(String)));
                    return unique.length > 0 ? (
                      <Box>
                        <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 1 }}>Full Stack</Typography>
                        <Box display="flex" flexWrap="wrap" gap={0.75}>
                          {unique.map((t) => (
                            <Chip key={t} label={t} size="small" sx={{ bgcolor: '#f8fafc', border: '1px solid #e2e8f0', fontWeight: 600, fontSize: '0.7rem' }} />
                          ))}
                        </Box>
                      </Box>
                    ) : null;
                  })()}
                </Box>
              </SectionCard>

              {/* Team & Enrollment */}
              <SectionCard title="Team & Enrollment" icon={UsersIcon}>
                <Box display="flex" flexDirection="column" gap={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="#64748b">Recommended Team Size</Typography>
                    <Typography variant="body2" fontWeight="700" color="#1e293b">
                      {project.default_team_size ? `${project.default_team_size} members` : 'Not provided'}
                    </Typography>
                  </Box>
                  {project.mentor_capacity ? (
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="body2" color="#64748b">Mentor Capacity</Typography>
                      <Typography variant="body2" fontWeight="700" color="#1e293b">{project.mentor_capacity}</Typography>
                    </Box>
                  ) : null}
                  <Box>
                    <Typography variant="caption" color="#94a3b8" fontWeight="600" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', display: 'block', mb: 1 }}>Engineering Roles</Typography>
                    {toList(project.preferred_roles).length > 0 ? (
                      <Box display="flex" flexWrap="wrap" gap={0.75}>
                        {toList(project.preferred_roles).map((r) => (
                          <Chip key={r} label={r} size="small" sx={{ bgcolor: '#eef2ff', color: '#4f46e5', fontWeight: 600, fontSize: '0.7rem' }} />
                        ))}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="#94a3b8" fontStyle="italic">Not provided</Typography>
                    )}
                  </Box>
                </Box>
              </SectionCard>

              {/* Evaluation criteria — hidden when nothing configured */}
              {(project.passing_score || project.min_ai_review_score || project.min_pr_approval || project.code_coverage_requirement
                || project.mandatory_ai_review || project.mandatory_peer_review) ? (
                <SectionCard title="Evaluation Criteria" icon={BarChart3}>
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {[
                      { label: 'Passing Score', value: project.passing_score != null && project.passing_score !== '' ? `${project.passing_score}%` : null },
                      { label: 'Min AI Review Score', value: project.min_ai_review_score != null && project.min_ai_review_score !== '' ? `${project.min_ai_review_score}%` : null },
                      { label: 'Min PR Approvals', value: project.min_pr_approval || null },
                      { label: 'Code Coverage', value: project.code_coverage_requirement != null && project.code_coverage_requirement !== '' ? `${project.code_coverage_requirement}%` : null },
                      { label: 'AI Review', value: project.mandatory_ai_review ? 'Mandatory' : null },
                      { label: 'Peer Review', value: project.mandatory_peer_review ? 'Mandatory' : null },
                    ].filter(i => i.value != null).map((i) => (
                      <Box key={i.label} display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="#64748b">{i.label}</Typography>
                        <Typography variant="body2" fontWeight="700" color="#1e293b">{i.value}</Typography>
                      </Box>
                    ))}
                  </Box>
                </SectionCard>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Box>

      {/* Versions Section */}
      <Box sx={{ px: 4, mt: 6 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box display="flex" alignItems="center" gap={1.5}>
              <Typography variant="h5" fontWeight="800" color="#1e293b">Project Versions</Typography>
              <Chip label={versions.length} size="small" sx={{ fontWeight: 'bold', bgcolor: '#f1f5f9' }} />
            </Box>
          </Box>
          
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
            <Table>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Version</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Sprint</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Schedule</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Release Target</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Team Size</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Enrollment</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', color: '#64748b', textTransform: 'uppercase', fontSize: '0.75rem' }} align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versions.length > 0 ? versions.map((v) => (
                  <TableRow key={v.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell>
                      <Typography fontWeight="700" color="#1e293b">{v.name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#475569">{v.sprint_duration || 'TBD'}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <Typography variant="body2" fontWeight="600" color="#1e293b">
                        {fmtDate(v.start_date) || 'TBD'} → {fmtDate(v.end_date) || 'TBD'}
                      </Typography>
                      <Typography variant="caption" color="#64748b">
                        Enroll: {fmtDate(v.enrollment_opens) || 'TBD'} – {fmtDate(v.enrollment_closes) || 'TBD'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#475569">{v.release_target ? new Date(v.release_target).toLocaleDateString() : 'TBD'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="#475569">{v.team_size || project.default_team_size || 25} members</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      {(() => {
                        const capacity = Math.min(parseInt(v.team_size, 10) || 25, 25);
                        const enrolled = parseInt(v.members_enrolled, 10) || 0;
                        const isFull = enrolled >= capacity;
                        const pct = capacity > 0 ? Math.min((enrolled / capacity) * 100, 100) : 0;
                        return (
                          <Box>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                              <Typography variant="body2" fontWeight="700" color={isFull ? '#dc2626' : '#1e293b'}>
                                {enrolled} / {capacity}
                              </Typography>
                              <Chip
                                label={v.enrollment_open ? 'Open' : 'Closed'}
                                size="small"
                                sx={{
                                  bgcolor: v.enrollment_open ? '#dcfce7' : '#f1f5f9',
                                  color: v.enrollment_open ? '#16a34a' : '#64748b',
                                  fontWeight: '700',
                                  borderRadius: '6px',
                                  fontSize: '0.7rem',
                                  height: 20
                                }}
                              />
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={pct}
                              sx={{
                                height: 6,
                                borderRadius: 3,
                                bgcolor: '#e2e8f0',
                                '& .MuiLinearProgress-bar': { bgcolor: isFull ? '#dc2626' : '#4f46e5', borderRadius: 3 }
                              }}
                            />
                          </Box>
                        );
                      })()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={v.status || 'Planning'}
                        size="small" 
                        sx={{ 
                          bgcolor: '#eef2ff',
                          color: '#4f46e5',
                          fontWeight: '700',
                          borderRadius: '6px',
                          textTransform: 'capitalize',
                          px: 1
                        }} 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="View Version Details">
                          <IconButton size="small" sx={{ color: '#6366f1', '&:hover': { bgcolor: '#eef2ff' } }} onClick={() => navigate(`/nextgen/versions/${v.id}`)}>
                            <Eye size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit Version & Schedule">
                          <IconButton size="small" sx={{ color: '#475569', '&:hover': { bgcolor: '#f1f5f9' } }} onClick={() => openEditVersion(v)}>
                            <Edit size={16} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete Version">
                          <IconButton
                            size="small"
                            sx={{ color: '#ef4444', '&:hover': { bgcolor: '#fef2f2' } }}
                            onClick={() => setDeleteDialog({ open: true, id: v.id, name: v.name })}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                      <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                        <Layers size={40} color="#cbd5e1" />
                        <Typography color="#64748b" fontWeight="500">No versions found for this project</Typography>
                        <Button variant="text" size="small" sx={{ color: '#4f46e5', fontWeight: '700' }} onClick={openCreateVersion}>
                          Create the first version
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
      </Box>

      {/* Add / Edit Version Dialog */}
      <Dialog open={openAddVersion} onClose={() => { setOpenAddVersion(false); setEditVersionId(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogTitle sx={{ fontWeight: '800', pt: 3, px: 3 }}>
          {editVersionId ? `Edit ${versionForm.name || 'Version'}` : 'Create New Version'}
        </DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          <Box display="flex" flexDirection="column" gap={2.5} sx={{ mt: 1 }}>
            {versionError && (
              <Alert severity="error" sx={{ borderRadius: '10px' }} onClose={() => setVersionError('')}>
                {versionError}
              </Alert>
            )}
            <TextField
              label="Version Name"
              fullWidth
              variant="outlined"
              placeholder="e.g. Version 1.0, Beta Release"
              value={versionForm.name}
              onChange={(e) => setVersionForm({...versionForm, name: e.target.value})}
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
            <TextField
              label="Sprint Duration"
              fullWidth
              variant="outlined"
              value={versionForm.sprint_duration}
              onChange={(e) => setVersionForm({...versionForm, sprint_duration: e.target.value})}
              placeholder="e.g. 2 weeks"
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
            <TextField
              label="Release Target Date"
              fullWidth
              type="date"
              variant="outlined"
              value={versionForm.release_target}
              onChange={(e) => setVersionForm({...versionForm, release_target: e.target.value})}
              InputLabelProps={{ shrink: true }}
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
            <TextField
              label="Team Size"
              fullWidth
              type="number"
              variant="outlined"
              value={versionForm.team_size}
              onChange={(e) => setVersionForm({...versionForm, team_size: e.target.value})}
              placeholder={`Default: ${project.default_team_size || 25}`}
              InputProps={{ sx: { borderRadius: '10px' } }}
            />

            {/* ── Batch Schedule ─────────────────────────────────────── */}
            <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1, mt: 0.5 }}>
              Batch Schedule — shown to students on the enrollment page
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <TextField
                label="Start Date" type="date" variant="outlined"
                value={versionForm.start_date}
                onChange={(e) => setVersionForm({ ...versionForm, start_date: e.target.value })}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }}
              />
              <TextField
                label="End Date" type="date" variant="outlined"
                value={versionForm.end_date}
                onChange={(e) => setVersionForm({ ...versionForm, end_date: e.target.value })}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }}
              />
              <TextField
                label="Enrollment Opens" type="date" variant="outlined"
                value={versionForm.enrollment_opens}
                onChange={(e) => setVersionForm({ ...versionForm, enrollment_opens: e.target.value })}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }}
                helperText="Enrollment auto-opens on this date"
              />
              <TextField
                label="Enrollment Closes" type="date" variant="outlined"
                value={versionForm.enrollment_closes}
                onChange={(e) => setVersionForm({ ...versionForm, enrollment_closes: e.target.value })}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }}
                helperText="After this the next version takes over"
              />
              <TextField
                label="Sprint Start" type="date" variant="outlined"
                value={versionForm.sprint_start_date}
                onChange={(e) => setVersionForm({ ...versionForm, sprint_start_date: e.target.value })}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }}
              />
              <TextField
                label="Sprint End" type="date" variant="outlined"
                value={versionForm.sprint_end_date}
                onChange={(e) => setVersionForm({ ...versionForm, sprint_end_date: e.target.value })}
                InputLabelProps={{ shrink: true }} InputProps={{ sx: { borderRadius: '10px' } }}
              />
            </Box>
            <TextField
              label="Maximum Members (seat cap)"
              fullWidth
              type="number"
              variant="outlined"
              value={versionForm.max_members}
              onChange={(e) => setVersionForm({ ...versionForm, max_members: e.target.value })}
              placeholder="Defaults to Team Size (max 25)"
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
            <TextField
              label="Description"
              fullWidth
              multiline
              rows={3}
              variant="outlined"
              value={versionForm.description}
              onChange={(e) => setVersionForm({...versionForm, description: e.target.value})}
              InputProps={{ sx: { borderRadius: '10px' } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => { setOpenAddVersion(false); setEditVersionId(null); }} sx={{ fontWeight: '700', color: '#64748b' }}>Cancel</Button>
          <Button
            onClick={handleSaveVersion}
            variant="contained"
            sx={{
              bgcolor: '#4f46e5',
              '&:hover': { bgcolor: '#4338ca' },
              borderRadius: '10px',
              fontWeight: '700',
              px: 3
            }}
          >
            {editVersionId ? 'Save Schedule' : 'Create Version'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
        PaperProps={{ sx: { borderRadius: '16px' } }}
      >
        <DialogTitle sx={{ fontWeight: '800', pt: 3 }}>Delete Version?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="#64748b">
            Are you sure you want to delete <strong>{deleteDialog.name}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setDeleteDialog({ open: false, id: null, name: '' })} sx={{ color: '#64748b', fontWeight: '700' }}>Cancel</Button>
          <Button onClick={handleDeleteVersion} variant="contained" color="error" sx={{ borderRadius: '10px', fontWeight: '700' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Mounted only while open so its fetch/hydrate cycle runs fresh each
          time, and so it costs nothing on the normal details view. */}
      {editOpen ? (
        <ProjectWizard
          open={editOpen}
          mode="edit"
          projectId={id}
          techStacks={techStacks}
          onClose={() => setEditOpen(false)}
          onSaved={handleProjectSaved}
        />
      ) : null}

      <Snackbar
        open={!!editToast}
        autoHideDuration={4000}
        onClose={() => setEditToast('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setEditToast('')} severity="success" variant="filled" sx={{ borderRadius: '10px', fontWeight: '600' }}>
          {editToast}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProjectDetails;
